import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { refreshApiView } from '@e3dc-react/shell/State/ApiView/ApiViewActions'
import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { Typography } from '@e3dc-react/shell/Elements/Typography'
import { DownloadsCategoryModel, DownloadsParent } from '../../models'
import { deleteCategory, getCategoryAndFileCount } from '../../api'
import { Box } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { CheckboxField } from '@e3dc-react/shell/Elements/InputFields'
import { useApiResultQuery } from 'src/Libs/api'
import { useTypedSelector } from 'src/State/RootReducer'
import { TextSkeleton } from '@e3dc-react/shell/Elements/Skeletons'
import { useNavigate } from 'react-router'
import { useCategoryDisplayName } from '../../hooks'

export interface DeleteCategoryProps {
  category: DownloadsCategoryModel
  parent?: DownloadsParent
  onClose: () => void
  closeEditModal: () => void
  selectCategory: (categoryId?: number) => void
}

const useStyles = makeStyles()(theme => ({
  dialogContent: {
    paddingBottom: 0,
  },
  contentWrapper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: theme.spacing(4),
  },
}))

const DeleteCategoryDialog: React.FC<DeleteCategoryProps> = ({ category, parent, closeEditModal, onClose, selectCategory }) => {
  const { t } = useCustomTranslation()
  const getCategoryDisplayName = useCategoryDisplayName()
  const { enqueueErrorStack, enqueueSuccessStack } = useSnackbar()
  const authFetch = useAuthFetchWithResponse()
  const dispatch = useDispatch()
  const refresh = useTypedSelector(state => state.apiView.refresh)
  const { classes } = useStyles()
  const [isRecursive, setIsRecursive] = useState<boolean>(false)
  const navigate = useNavigate()

  const { data, isLoading } = useApiResultQuery({
    queryKey: ['download-files', category.id, refresh],
    fetchFunction: () => getCategoryAndFileCount(category.id, authFetch),
    options: {
      staleTime: 0,
    },
  })

  const onDeleteCategory = (): void => {
    if (category)
      deleteCategory(category?.id, isRecursive, authFetch)
        .then(() => {
          selectCategory(parent ? parent.id : undefined)
          navigate(parent ? `/downloads/${parent.id}` : '/downloads')
          enqueueSuccessStack(t('documents.delete-category.success'))
        })
        .catch(error => {
          enqueueErrorStack(t('documents.delete-category.error'))
          console.error('Error deleting category:', error)
        })
        .finally(() => dispatch(refreshApiView()))
    onClose()
    closeEditModal()
  }

  return (
    <Dialog
      maxWidth="sm"
      title={t('documents.deleteCategory.confitmationTitle')}
      actions={{
        primary: {
          name: t('documents.delete-category.confirm'),
          onClick: () => onDeleteCategory(),
        },
        secondary: {
          name: t('common.cancel'),
          onClick: () => onClose(),
        },
      }}
      contentClassName={classes.dialogContent}
    >
      {isLoading ? (
        <TextSkeleton variant="body1" />
      ) : (
        <Box className={classes.contentWrapper}>
          <Typography values={{ name: getCategoryDisplayName(category.nameLanguages) }}>{'documents.delete-category.confrimationText'}</Typography>
          <Typography variant={'body2'} values={{ fileCount: data?.fileCount ?? '', categoryCount: data?.categoryCount ?? '' }}>
            {'documents.delete-category.hint'}
          </Typography>
          <CheckboxField onChange={() => setIsRecursive(!isRecursive)} value={isRecursive} label={t('documents.delete-category.confirm-recursive')} />
        </Box>
      )}
    </Dialog>
  )
}

export default DeleteCategoryDialog
